import React, { useState, useEffect } from 'react';
import { Container, Tabs, Tab, Table, Button, Form, Spinner, Badge, Card } from 'react-bootstrap';
import { logService } from '../services/api';

const LogsPage = () => {
  const [activeTab, setActiveTab] = useState('power');
  const [powerLogs, setPowerLogs] = useState([]);
  const [systemLogs, setSystemLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(100);
  const [error, setError] = useState(null);

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'power') {
        const data = await logService.getPowerLogs(limit);
        setPowerLogs(data);
      } else {
        const data = await logService.getSystemLogs(limit);
        setSystemLogs(data);
      }
    } catch (err) {
      console.error('Failed to fetch logs:', err);
      setError('Failed to fetch logs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [activeTab, limit]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const getActionBadge = (action) => {
    switch (action) {
      case 'wake':
        return <Badge bg="success" className="text-uppercase tracking-wider px-2 py-1 fs-8">Wake</Badge>;
      case 'shutdown':
        return <Badge bg="danger" className="text-uppercase tracking-wider px-2 py-1 fs-8">Shutdown</Badge>;
      case 'restart':
        return <Badge bg="warning" className="text-dark text-uppercase tracking-wider px-2 py-1 fs-8">Restart</Badge>;
      case 'ping':
        return <Badge bg="info" className="text-uppercase tracking-wider px-2 py-1 fs-8">Ping</Badge>;
      default:
        return <Badge bg="secondary" className="text-uppercase tracking-wider px-2 py-1 fs-8">{action}</Badge>;
    }
  };

  const getStatusBadge = (status) => {
    return status === 'success' ? (
      <span className="text-emerald-400 d-flex align-items-center gap-1">
        <i className="fas fa-check-circle text-success fs-7"></i> Success
      </span>
    ) : (
      <span className="text-rose-400 d-flex align-items-center gap-1">
        <i className="fas fa-times-circle text-danger fs-7"></i> Failed
      </span>
    );
  };

  const getLevelBadge = (level) => {
    switch (level) {
      case 'info':
        return <Badge bg="info" className="px-2 py-1 fs-8 text-uppercase">Info</Badge>;
      case 'warning':
        return <Badge bg="warning" className="text-dark px-2 py-1 fs-8 text-uppercase">Warning</Badge>;
      case 'error':
        return <Badge bg="danger" className="px-2 py-1 fs-8 text-uppercase">Error</Badge>;
      default:
        return <Badge bg="secondary" className="px-2 py-1 fs-8 text-uppercase">{level}</Badge>;
    }
  };

  return (
    <div className="py-4" style={{ minHeight: '100vh', paddingLeft: '2rem', paddingRight: '2rem' }}>
      <Container fluid className="px-0">
        
        {/* Header Section */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
          <div>
            <h1 className="mb-1 text-theme-main" style={{ fontSize: '1.75rem', fontWeight: 700 }}>
              <i className="fas fa-history me-2 text-primary"></i>
              Audit & Event Logs
            </h1>
            <p className="text-theme-muted mb-0" style={{ fontSize: '0.9rem' }}>
              Monitor remote actions, cluster triggers, and system logs across your network
            </p>
          </div>
          
          <div className="d-flex align-items-center gap-3">
            <Form.Group className="d-flex align-items-center gap-2 mb-0">
              <Form.Label className="text-theme-muted mb-0 fs-7 text-nowrap">Show Limit:</Form.Label>
              <Form.Select
                size="sm"
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="form-input-premium py-1"
                style={{ width: '90px' }}
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </Form.Select>
            </Form.Group>
            
            <Button
              variant="premium-primary"
              onClick={fetchLogs}
              disabled={loading}
              className="d-flex align-items-center gap-2"
            >
              <i className={`fas fa-sync ${loading ? 'fa-spin' : ''}`}></i>
              Refresh Logs
            </Button>
          </div>
        </div>

        {error && (
          <Card className="border-0 mb-4 bg-rose-950 bg-opacity-20 text-rose-400">
            <Card.Body className="d-flex align-items-center gap-2 py-3">
              <i className="fas fa-exclamation-triangle"></i>
              {error}
            </Card.Body>
          </Card>
        )}

        <div className="glass-card p-3">
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-4 custom-tabs border-0"
          >
            <Tab 
              eventKey="power" 
              title={<span><i className="fas fa-plug me-2"></i>Power & Control Events</span>}
            >
              {loading ? (
                <div className="text-center py-5 text-theme-muted">
                  <Spinner animation="border" variant="primary" className="mb-3" />
                  <p>Loading power events...</p>
                </div>
              ) : powerLogs.length === 0 ? (
                <div className="text-center py-5 text-theme-muted">
                  <i className="fas fa-inbox fa-3x mb-3 text-muted"></i>
                  <p>No power events logged yet.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table className="table-premium align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Timestamp</th>
                        <th>Target Machine</th>
                        <th>Action</th>
                        <th>Status</th>
                        <th>Initiated By</th>
                        <th>Response Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {powerLogs.map((log) => (
                        <tr key={log.id}>
                          <td style={{ whiteSpace: 'nowrap' }} className="fs-7 text-theme-muted">
                            {formatDate(log.created_at)}
                          </td>
                          <td className="fw-semibold text-theme-main">
                            {log.machine_id ? (
                              <div>
                                <span>{log.machine_id.name}</span>
                                <br />
                                <span className="fs-8 text-theme-muted fw-normal">
                                  {log.machine_id.ip_address} | {log.machine_id.mac_address}
                                </span>
                              </div>
                            ) : (
                              <span className="text-danger fs-8">Machine Deleted</span>
                            )}
                          </td>
                          <td>{getActionBadge(log.action)}</td>
                          <td>{getStatusBadge(log.status)}</td>
                          <td className="text-theme-muted fs-7">
                            <i className="fas fa-user-circle me-1"></i> {log.initiated_by}
                          </td>
                          <td className="text-theme-muted fs-7">
                            {log.response_time !== null ? `${log.response_time}ms` : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Tab>

            <Tab 
              eventKey="system" 
              title={<span><i className="fas fa-shield-alt me-2"></i>System Security Logs</span>}
            >
              {loading ? (
                <div className="text-center py-5 text-theme-muted">
                  <Spinner animation="border" variant="primary" className="mb-3" />
                  <p>Loading system security logs...</p>
                </div>
              ) : systemLogs.length === 0 ? (
                <div className="text-center py-5 text-theme-muted">
                  <i className="fas fa-inbox fa-3x mb-3 text-muted"></i>
                  <p>No system logs logged yet.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table className="table-premium align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Timestamp</th>
                        <th>Level</th>
                        <th>Category</th>
                        <th>Message</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {systemLogs.map((log) => (
                        <tr key={log.id}>
                          <td style={{ whiteSpace: 'nowrap' }} className="fs-7 text-theme-muted">
                            {formatDate(log.created_at)}
                          </td>
                          <td>{getLevelBadge(log.level)}</td>
                          <td>
                            <Badge bg="secondary" className="px-2 py-1 fs-8 text-uppercase">{log.category}</Badge>
                          </td>
                          <td className="text-theme-main fw-semibold fs-7">{log.message}</td>
                          <td className="text-theme-muted fs-8 font-monospace" style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={log.details}>
                            {log.details || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Tab>
          </Tabs>
        </div>
      </Container>
    </div>
  );
};

export default LogsPage;
